/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './FormDesign.scss';

import { Label } from '@fluentui/react/lib/Label';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { Constants } from '../../../../../common/Constants';
import { FormDesignAdapter } from '../../../../../forms';
import { LogCategory } from '../../../../../logger';
import { FormsIframeMessageType, FormsIframeSavingStatus } from '../../../../../models/formsIframe/FormsIframe';
import {
    CommonErrorMessageType,
    createPostMessageSender,
    PostMessage,
    setupPostMessageReceiver,
} from '../../../../../utilities/IFrameUtils';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { errorConvert, ErrorEvent } from '../../../../../utilities/TemplateUtils';
import { getFormsEndpoint } from '../../../../../utilities/Utils';
import { ErrorPage, ErrorPageProps } from '../../../../containers/ErrorPage';
import { LoadingPage } from '../../../../containers/LoadingPage';
import { getTitleStyles } from '../../CreateTemplateInstanceWizard.styles';
import {
    getDescriptionStyles,
    getFormContainerStyles,
    getFormsDesignTimeIframeStyles,
    getMessageBarStyles,
    getStackStyles,
    getSubStackStyles,
} from './FormDesign.styles';

export interface FormDesignComponentProps {
    isLoading: boolean;
    setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    onFormReady: () => void;
    error: ErrorPageProps;
    setError?: React.Dispatch<React.SetStateAction<ErrorPageProps>>;
    onFormError: (event: ErrorEvent) => void;
    onSavingStatusChange?: (status: FormsIframeSavingStatus) => void;
    onFormFocus?: () => void;
    formId: string;
    showWarning?: boolean;
    onWarningClosed?: () => void;
    retryCount: number;
    isSelfOwnedFormEnabled: boolean;
}

export const FormDesignComponent: React.FC<FormDesignComponentProps> = (
    props: FormDesignComponentProps
): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const loc = host.getLocale();
    const localeName = host.getLocaleName();
    const IsFormV2ApiEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.IsFormV2ApiEnabled;

    const themeName = host.getTheme();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const formsIframeRef = React.useRef<HTMLIFrameElement>(null);
    const {
        isLoading,
        setIsLoading,
        onFormReady,
        error,
        onFormError,
        onSavingStatusChange,
        onFormFocus,
        formId,
        showWarning,
        onWarningClosed,
        retryCount,
        isSelfOwnedFormEnabled,
    } = props;

    const getFormsDesignTimeIframeUrl = (): string => {
        const formsIframeUrl = new URL(Constants.FORMS_DESIGNTIME_IFRAME_PATH, getFormsEndpoint());
        formsIframeUrl.searchParams.append('Id', `${formId}`);
        formsIframeUrl.searchParams.append('Host', Constants.FORMS_APPROVAL_APP_NAME);
        // Forms will not strongly depend on the auth param for now.
        formsIframeUrl.searchParams.append('auth_pvr', 'AAD');
        formsIframeUrl.searchParams.append('auth_upn', host.getUserPrincipalName());
        formsIframeUrl.searchParams.append('theme', themeName);
        formsIframeUrl.searchParams.append('lang', localeName);
        formsIframeUrl.searchParams.append('tid', host.getTenantId());

        if (IsFormV2ApiEnabled) {
            formsIframeUrl.searchParams.append('version', '2.0');
        }

        return formsIframeUrl.toString();
    };
    const formDesignTimeIframeUrl = React.useMemo<string>((): string => {
        return getFormsDesignTimeIframeUrl();
    }, [formId]);

    React.useEffect((): any => {
        if (!formsIframeRef.current) {
            return;
        }

        const formsEndpoint = getFormsEndpoint();

        const postMessageSender = createPostMessageSender(window, formsIframeRef.current.contentWindow, formsEndpoint);
        const formsIframeMessageHandler = {
            [IsFormV2ApiEnabled ? FormsIframeMessageType.FormReadyV2 : FormsIframeMessageType.FormReady]: (
                _message: PostMessage<FormsIframeMessageType>
            ): void => {
                onFormReady();
            },
            [CommonErrorMessageType]: (message: PostMessage<FormsIframeMessageType>): void => {
                onFormError(errorConvert(message));
            },
            [FormsIframeMessageType.SavingStatus]: (message: PostMessage<FormsIframeMessageType>): void => {
                if (onSavingStatusChange) {
                    onSavingStatusChange(message.payload.status);
                }
            },
            [FormsIframeMessageType.GetSetting]: (message: PostMessage<FormsIframeMessageType>): void => {
                postMessageSender.send({ type: FormsIframeMessageType.Setting, payload: { themeMode: themeName } });
            },
            [FormsIframeMessageType.GetTokenRequest]: (message: PostMessage<FormsIframeMessageType>): void => {
                logger.logTraceInfo(LogCategory.TemplateInstanceWizard, 'Form design time iframe is retrieving token');
                host.getAuthToken(formsEndpoint).then((token: string): void => {
                    logger.logTraceInfo(LogCategory.TemplateInstanceWizard, 'Passing token to form design time iframe');
                    postMessageSender.sendReply(message, { payload: { token } });

                    // in this function, the postMessageSender is initalized and can receive the message
                    postMessageSender.send({
                        type: IsFormV2ApiEnabled
                            ? FormsIframeMessageType.ThemeChangedV2
                            : FormsIframeMessageType.ThemeChanged,
                        payload: { themeMode: themeName },
                    });
                });
            },
        };

        const { remove } = setupPostMessageReceiver(window, formsEndpoint, formsIframeMessageHandler);
        return remove;
    }, [retryCount, onSavingStatusChange, setIsLoading, formsIframeRef.current]);

    React.useEffect((): void => {
        if (!formsIframeRef.current) {
            return;
        }

        const postMessageSender = createPostMessageSender(
            window,
            formsIframeRef.current.contentWindow,
            getFormsEndpoint()
        );
        postMessageSender.send({
            type: IsFormV2ApiEnabled ? FormsIframeMessageType.ThemeChangedV2 : FormsIframeMessageType.ThemeChanged,
            payload: { themeMode: themeName },
        });
    }, [themeName, formsIframeRef.current]);

    // Since cross domain iframe event will not bubble up|down, use the following approach to simulate iframe focus
    React.useEffect((): any => {
        const onBlur = (): void => {
            if (onFormFocus && document.activeElement === formsIframeRef.current) {
                onFormFocus();
            }
        };

        window.addEventListener('blur', onBlur);

        return (): void => {
            window.removeEventListener('blur', onBlur);
        };
    }, [onFormFocus]);

    /* eslint-disable @microsoft/sdl/react-iframe-missing-sandbox */
    return (
        <>
            {showWarning && (
                <MessageBar
                    styles={getMessageBarStyles(theme)}
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    onDismiss={onWarningClosed}>
                    {loc.strings.edit_form_warning}
                </MessageBar>
            )}

            <Stack styles={getStackStyles}>
                <Label
                    id='form-design-label'
                    aria-label={loc.strings.form_design_wizard_step_label}
                    styles={getTitleStyles(theme)}>
                    {loc.strings.form_design_wizard_step_label}
                </Label>
                <Label
                    id='form-design-desc'
                    aria-label={loc.strings.form_design_wizard_step_description}
                    styles={getDescriptionStyles(theme)}>
                    {loc.strings.form_design_wizard_step_description}
                </Label>
                {isSelfOwnedFormEnabled && (
                    <FormDesignAdapter className='form-design-adapter' onFormReady={onFormReady} />
                )}
                <Stack className='form-designer-wrapper' styles={getFormContainerStyles}>
                    {!isSelfOwnedFormEnabled && formId && (
                        <iframe
                            title={loc.strings.forms_iframe_title}
                            key={retryCount}
                            ref={formsIframeRef}
                            src={formDesignTimeIframeUrl}
                            style={getFormsDesignTimeIframeStyles()}
                        />
                    )}

                    {isLoading && (
                        <Stack styles={getSubStackStyles(theme)}>
                            <LoadingPage />
                        </Stack>
                    )}
                    {error && (
                        <Stack styles={getSubStackStyles(theme)}>
                            <ErrorPage {...error} />
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </>
    );
    /* eslint-enable @microsoft/sdl/react-iframe-missing-sandbox */
};
